import React from 'react';
import './Pagination.css';

/**
 * A reusable pagination component
 * 
 * Props:
 * - currentPage: Current page number (1-indexed)
 * - totalPages: Total number of pages
 * - onPageChange: Callback when page changes (receives new page number)
 * - isLoading: Optional loading state to disable buttons
 * - showPageNumbers: Optional, show page numbers (default: true)
 * - maxVisiblePages: Optional, max visible page numbers (default: 5)
 */
const Pagination = ({
    currentPage = 1,
    totalPages = 1,
    onPageChange,
    isLoading = false,
    showPageNumbers = true,
    maxVisiblePages = 5
}) => {
    if (totalPages <= 1) return null;

    const handlePrev = () => {
        if (currentPage > 1 && !isLoading) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages && !isLoading) {
            onPageChange(currentPage + 1);
        }
    };

    const handlePageClick = (page) => {
        if (page !== currentPage && !isLoading) {
            onPageChange(page);
        }
    };

    // Calculate visible page numbers
    const getVisiblePages = () => {
        const pages = [];
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        // Adjust if we're near the end
        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    const visiblePages = getVisiblePages();

    return (
        <div className="pagination-container">
            <div className="pagination-info">
                Page {currentPage} of {totalPages}
            </div>

            <div className="pagination-controls">
                <button
                    className="pagination-btn pagination-prev"
                    onClick={handlePrev}
                    disabled={currentPage === 1 || isLoading}
                    aria-label="Previous page"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    <span className="pagination-btn-text">Prev</span>
                </button>

                {showPageNumbers && (
                    <div className="pagination-pages">
                        {visiblePages[0] > 1 && (
                            <>
                                <button
                                    className={`pagination-page ${currentPage === 1 ? 'active' : ''}`}
                                    onClick={() => handlePageClick(1)}
                                    disabled={isLoading}
                                >
                                    1
                                </button>
                                {visiblePages[0] > 2 && <span className="pagination-ellipsis">...</span>}
                            </>
                        )}

                        {visiblePages.map(page => (
                            <button
                                key={page}
                                className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                                onClick={() => handlePageClick(page)}
                                disabled={isLoading}
                            >
                                {page}
                            </button>
                        ))}

                        {visiblePages[visiblePages.length - 1] < totalPages && (
                            <>
                                {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                                    <span className="pagination-ellipsis">...</span>
                                )}
                                <button
                                    className={`pagination-page ${currentPage === totalPages ? 'active' : ''}`}
                                    onClick={() => handlePageClick(totalPages)}
                                    disabled={isLoading}
                                >
                                    {totalPages}
                                </button>
                            </>
                        )}
                    </div>
                )}

                <button
                    className="pagination-btn pagination-next"
                    onClick={handleNext}
                    disabled={currentPage === totalPages || isLoading}
                    aria-label="Next page"
                >
                    <span className="pagination-btn-text">Next</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default Pagination;
